import * as BaseTooltip from "@radix-ui/react-tooltip";
import { cx } from "class-variance-authority";
import React from "react";

interface TooltipProps
  extends Omit<
    BaseTooltip.TooltipContentProps,
    // overriding this typing
    "content"
  > {
  children: React.ReactNode;
  content: (() => React.ReactNode) | string;
  arrowClass?: string;
  delayDuration?: number;
  disabled?: boolean;
}

const defaultClassName = cx(
  "rounded shadow-md pointer-events-none select-none",
  "text-center text-sm text-white",
  "px-3 py-1",
  "bg-charcoal",
);

/**
 * @deprecated in FloKit
 */
const Tooltip: React.FC<TooltipProps> = ({
  children,
  disabled,
  content,
  arrowClass,
  className,
  align,
  side,
  ...props
}) => {
  const contentClassName = className ? className : defaultClassName;
  const arrowClassName = arrowClass ? arrowClass : "text-charcoal";
  const alignment = align ? align : "center";
  const tooltipSide = side ? side : "top";
  return (
    <BaseTooltip.Root delayDuration={props.delayDuration ?? 0}>
      {disabled ? (
        children
      ) : (
        <React.Fragment>
          <BaseTooltip.Trigger asChild>
            <span className="appearance-none">{children}</span>
          </BaseTooltip.Trigger>
          <BaseTooltip.Content
            {...props}
            side={tooltipSide}
            align={alignment}
            className={contentClassName}
            style={{
              maxWidth: "300px",
            }}
          >
            {typeof content === "string" ? content : content ? content() : "-"}
            <BaseTooltip.Arrow
              className={cx("fill-current", arrowClassName)}
              offset={5}
              width={11}
              height={5}
            />
          </BaseTooltip.Content>
        </React.Fragment>
      )}
    </BaseTooltip.Root>
  );
};

export default Tooltip;

interface OverflowTooltipProps extends Pick<TooltipProps, "side"> {
  values: (string | React.ReactNode)[];
  triggerClassName?: string;
  keyFunction?: (val: string | React.ReactNode) => string;
}
export const OverflowTooltip = (props: OverflowTooltipProps) => {
  if (props.values.length === 0) return null;

  return (
    <Tooltip
      side={props.side}
      content={() => {
        return (
          <ol>
            {props.values.map((val, idx) => {
              const key = props?.keyFunction ? props.keyFunction(val) : idx;
              return <li key={key}>{val}</li>;
            })}
          </ol>
        );
      }}
    >
      <span className={props.triggerClassName}>+{props.values.length}</span>
    </Tooltip>
  );
};

export const OVERFLOW_LIST_PLACEHOLDER = "TBD";

interface OverflowListProps {
  values: string[];
  keyFunction?: (val: string | React.ReactNode) => string;
  textClass?: string;
  triggerClassName?: string;
  placeholder?: string;
}
export const OverflowList = (props: OverflowListProps) => {
  return (
    <span className={cx(props?.textClass ?? "")}>
      {props.values.length === 0
        ? (props.placeholder ?? OVERFLOW_LIST_PLACEHOLDER)
        : props.values[0]}
      {props.values.length > 1 && (
        <span>
          &nbsp;
          <OverflowTooltip
            keyFunction={props.keyFunction}
            values={props.values.slice(1)}
            triggerClassName={cx(
              props.triggerClassName ?? "text-navy font-medium cursor-pointer",
            )}
          />
        </span>
      )}
    </span>
  );
};
